import React from 'react';
import { Composition } from 'remotion';
import { MetaAd } from './MetaAd';
import { FeedSquareAd, FeedPortraitAd } from './MetaAd/FeedAd';
import { ReelsAd } from './MetaAd/ReelsAd';
import { GDMQuietPhoneV3 } from './GDMQuietPhoneV3';
import { GDMQuietPhoneV4 } from './GDMQuietPhoneV4';
import { GDMQuietPhoneV5 } from './GDMQuietPhoneV5';
import { PVMQuietPhoneV1 } from './PVMQuietPhoneV1';
import { PVMQuietPhoneV3 } from './PVMQuietPhoneV3';
import { GDMInvisibleTest } from './GDMInvisibleTest';
import { PVMInvisibleTest } from './PVMInvisibleTest';
import { GDMWhereDoYouRank } from './GDMWhereDoYouRank';
import { GDM3To18Jobs } from './GDM3To18Jobs';
import { GDMEveryMissedCall } from './GDMEveryMissedCall';
import { GDMLastAgency } from './GDMLastAgency';
import { PVMStopFeedingSharedLeads } from './PVMStopFeedingSharedLeads';
import { PVM3800To12000 } from './PVM3800To12000';
import { PVMSecondMonthPause } from './PVMSecondMonthPause';
import { AIPlayer } from './AIPlayer';
import { SlideDeck } from './SlideDeck';
import { TalkingHeadEnhanced } from './TalkingHeadEnhanced';
import {
  MetaAdPropsSchema,
  ASPECT_RATIOS,
  getDefaultProps,
  COMP_NAMES,
  VIDEO_FPS,
} from './schemas';
import { AIPlayerPropsSchema, getDefaultAIVideoSpec, getAIVideoMetrics } from './ai-schema';
import { SlideDeckPropsSchema, getDefaultSlideDeckSpec, getSlideDeckMetrics } from './SlideDeck/schema';
import { HeroSquare, HeroVertical } from './Hero';
import { HeroPropsSchema, HERO_DIMENSIONS, getDefaultHeroProps } from './Hero/schema';
import { calculateHeroMetadata } from './Hero/timeline';
import { loadBrandFonts } from './brand/fonts';

// Ensure fonts are available for all compositions (Studio + renders).
loadBrandFonts();

const DEFAULT_DURATION = 300;
const TALKING_HEAD_FPS = 25;

// Aspect ratio variants for scene-based compositions
type AspectRatio = '1x1' | '4x5' | '9x16' | '16x9';
const ASPECT_DIMENSIONS: Record<AspectRatio, { width: number; height: number }> = {
  '1x1': { width: 1080, height: 1080 },
  '4x5': { width: 1080, height: 1350 },
  '9x16': { width: 1080, height: 1920 },
  '16x9': { width: 1920, height: 1080 },
};

// Registry of scene-based compositions with their aspect ratio variants
interface SceneComposition {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  durationInFrames: number;
  variants: AspectRatio[];
}

const SCENE_COMPOSITIONS: SceneComposition[] = [
  { id: 'GDMQuietPhoneV3', component: GDMQuietPhoneV3, durationInFrames: 800, variants: ['1x1'] },
  { id: 'GDMQuietPhoneV4', component: GDMQuietPhoneV4, durationInFrames: 772, variants: ['1x1'] },
  { id: 'GDMQuietPhoneV5', component: GDMQuietPhoneV5, durationInFrames: 966, variants: ['1x1', '9x16', '16x9'] },
  { id: 'PVMQuietPhoneV1', component: PVMQuietPhoneV1, durationInFrames: 899, variants: ['1x1', '9x16'] },
  { id: 'PVMQuietPhoneV3', component: PVMQuietPhoneV3, durationInFrames: 995, variants: ['1x1', '9x16', '16x9'] },
  { id: 'GDMInvisibleTest', component: GDMInvisibleTest, durationInFrames: 870, variants: ['1x1', '4x5', '9x16', '16x9'] },
  { id: 'PVMInvisibleTest', component: PVMInvisibleTest, durationInFrames: 835, variants: ['1x1', '4x5', '9x16', '16x9'] },
  { id: 'GDMWhereDoYouRank', component: GDMWhereDoYouRank, durationInFrames: 870, variants: ['1x1'] },
  { id: 'GDM3To18Jobs', component: GDM3To18Jobs, durationInFrames: 866, variants: ['1x1'] },
  { id: 'GDMEveryMissedCall', component: GDMEveryMissedCall, durationInFrames: 766, variants: ['1x1'] },
  { id: 'GDMLastAgency', component: GDMLastAgency, durationInFrames: 686, variants: ['1x1'] },
  { id: 'PVMStopFeedingSharedLeads', component: PVMStopFeedingSharedLeads, durationInFrames: 847, variants: ['1x1'] },
  { id: 'PVM3800To12000', component: PVM3800To12000, durationInFrames: 828, variants: ['1x1'] },
  { id: 'PVMSecondMonthPause', component: PVMSecondMonthPause, durationInFrames: 767, variants: ['1x1', '9x16'] },
];

export const RemotionRoot: React.FC = () => {
  return (
    <>
      {/* Meta Ad compositions */}
      <Composition
        id={COMP_NAMES.FEED_SQUARE}
        component={FeedSquareAd}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['1:1'].width}
        height={ASPECT_RATIOS['1:1'].height}
        schema={MetaAdPropsSchema}
        defaultProps={getDefaultProps('1:1')}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: props.durationInFrames || DEFAULT_DURATION,
        })}
      />
      <Composition
        id={COMP_NAMES.FEED_PORTRAIT}
        component={FeedPortraitAd}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['4:5'].width}
        height={ASPECT_RATIOS['4:5'].height}
        schema={MetaAdPropsSchema}
        defaultProps={getDefaultProps('4:5')}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: props.durationInFrames || DEFAULT_DURATION,
        })}
      />
      <Composition
        id={COMP_NAMES.REELS}
        component={ReelsAd}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['9:16'].width}
        height={ASPECT_RATIOS['9:16'].height}
        schema={MetaAdPropsSchema}
        defaultProps={getDefaultProps('9:16')}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: props.durationInFrames || DEFAULT_DURATION,
        })}
      />
      <Composition
        id="MetaAd"
        component={MetaAd}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={1080}
        height={1080}
        schema={MetaAdPropsSchema}
        defaultProps={getDefaultProps('1:1')}
        calculateMetadata={async ({ props }) => {
          const aspectRatio = props.aspectRatio || '1:1';
          const dimensions = ASPECT_RATIOS[aspectRatio];
          return {
            width: dimensions.width,
            height: dimensions.height,
            durationInFrames: props.durationInFrames || DEFAULT_DURATION,
          };
        }}
      />

      {/* Video-Landscape composition (16:9 for X/Twitter) */}
      <Composition
        id={COMP_NAMES.LANDSCAPE}
        component={MetaAd}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['16:9'].width}
        height={ASPECT_RATIOS['16:9'].height}
        schema={MetaAdPropsSchema}
        defaultProps={getDefaultProps('16:9')}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: props.durationInFrames || DEFAULT_DURATION,
        })}
      />

      {/* Scene-based compositions (data-driven with aspect ratio variants) */}
      {SCENE_COMPOSITIONS.map(({ id, component, durationInFrames, variants }) =>
        variants.map((variant) => {
          const { width, height } = ASPECT_DIMENSIONS[variant];
          const compositionId = variant === '1x1' ? id : `${id}-${variant}`;
          return (
            <Composition
              key={compositionId}
              id={compositionId}
              component={component}
              durationInFrames={durationInFrames}
              fps={VIDEO_FPS}
              width={width}
              height={height}
            />
          );
        })
      )}

      {/* AI-driven spec renderer */}
      <Composition
        id="AIPlayer"
        component={AIPlayer}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['1:1'].width}
        height={ASPECT_RATIOS['1:1'].height}
        schema={AIPlayerPropsSchema}
        defaultProps={{ spec: getDefaultAIVideoSpec() }}
        calculateMetadata={async ({ props }) => {
          const { width, height, fps, durationInFrames } = getAIVideoMetrics(props.spec);
          return { width, height, fps, durationInFrames };
        }}
      />

      {/* HG Content SlideSpec renderer */}
      <Composition
        id="SlideDeck"
        component={SlideDeck}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={ASPECT_RATIOS['16:9'].width}
        height={ASPECT_RATIOS['16:9'].height}
        schema={SlideDeckPropsSchema}
        defaultProps={{ spec: getDefaultSlideDeckSpec() }}
        calculateMetadata={async ({ props }) => getSlideDeckMetrics(props)}
      />

      {/* Talking-head enhancer (blur-fill + karaoke captions) */}
      <Composition
        id="TalkingHeadEnhanced"
        component={TalkingHeadEnhanced}
        durationInFrames={DEFAULT_DURATION}
        fps={TALKING_HEAD_FPS}
        width={ASPECT_RATIOS['1:1'].width}
        height={ASPECT_RATIOS['1:1'].height}
        defaultProps={{ videoUrl: '' }}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: (props as { durationInFrames?: number }).durationInFrames || DEFAULT_DURATION,
        })}
      />
      <Composition
        id="TalkingHeadEnhanced-9x16"
        component={TalkingHeadEnhanced}
        durationInFrames={DEFAULT_DURATION}
        fps={TALKING_HEAD_FPS}
        width={ASPECT_RATIOS['9:16'].width}
        height={ASPECT_RATIOS['9:16'].height}
        defaultProps={{ videoUrl: '' }}
        calculateMetadata={async ({ props }) => ({
          durationInFrames: (props as { durationInFrames?: number }).durationInFrames || DEFAULT_DURATION,
        })}
      />

      {/* Hero assembly masters (generated clips + bookends + thread + type + captions + audio + logo close) */}
      <Composition
        id="HeroSquare"
        component={HeroSquare}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={HERO_DIMENSIONS['1:1'].width}
        height={HERO_DIMENSIONS['1:1'].height}
        schema={HeroPropsSchema}
        defaultProps={getDefaultHeroProps('1:1')}
        calculateMetadata={calculateHeroMetadata}
      />
      <Composition
        id="HeroVertical"
        component={HeroVertical}
        durationInFrames={DEFAULT_DURATION}
        fps={VIDEO_FPS}
        width={HERO_DIMENSIONS['9:16'].width}
        height={HERO_DIMENSIONS['9:16'].height}
        schema={HeroPropsSchema}
        defaultProps={getDefaultHeroProps('9:16')}
        calculateMetadata={calculateHeroMetadata}
      />
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
